import type { PlayerControlSize } from "app/player/controlSizes"
import { cn } from "app/lib/utils"
import { getVolumeIcon, type PlayerVolumeColor } from "app/player/getVolumeIcon"
import { motion } from "framer-motion"
import { useCallback, useEffect, useId, useRef, useState } from "react"
import { poppins } from "theme/fonts"
import PlayerIcon from "./PlayerIcon"
import PlayerVolumeTrack from "./PlayerVolumeTrack"
import {
  blueVolumeIconButtonActiveClassName,
  blueVolumeIconButtonBaseClassName,
  blueVolumeIconButtonDefaultClassName,
  blueVolumeIconButtonSizeClassName,
  containerVariants,
  dividerVariants,
  iconSectionVariants,
  labelVariants,
  playerIconButtonInnerClassName,
  VOLUME_COLLAPSE_DURATION,
  whiteVolumeIconButtonActiveClassName,
  whiteVolumeIconButtonDefaultClassName,
} from "./playerVolumeStyles"
import { useCloseOnOutsidePointerDown } from "./useCloseOnOutsidePointerDown"
import { usePlayerVolumeMute } from "./usePlayerVolumeMute"

function volumeIconButtonStateClassName(
  color: PlayerVolumeColor,
  isMuted: boolean
) {
  if (color === "white") {
    return isMuted
      ? whiteVolumeIconButtonActiveClassName
      : whiteVolumeIconButtonDefaultClassName
  }

  return isMuted
    ? blueVolumeIconButtonActiveClassName
    : blueVolumeIconButtonDefaultClassName
}

type ControlSize = PlayerControlSize

type Props = {
  volume: number
  onVolumeChange: (volume: number) => void
  color?: PlayerVolumeColor
  defaultExpanded?: boolean
  controlsVisible?: boolean
  controlsHideDelayMs?: number
  size?: ControlSize
  /** iOS: volume slider is inert; show mute toggle only. */
  muteOnly?: boolean
}

function VolumeIconSection({
  color,
  volume,
  isMuted,
  onToggleMute,
  size = "md",
}: {
  color: PlayerVolumeColor
  volume: number
  isMuted: boolean
  onToggleMute: () => void
  size?: ControlSize
}) {
  return (
    <div className="flex h-full shrink-0 items-center">
      <button
        type="button"
        className={cn(
          iconSectionVariants({ color, muted: isMuted, size }),
          "rounded-bl-[12px] rounded-tl-[12px]"
        )}
        aria-label={isMuted ? "Unmute" : "Mute"}
        aria-pressed={isMuted}
        onClick={(event) => {
          event.stopPropagation()
          onToggleMute()
        }}
      >
        <span
          className={cn(
            "flex size-full items-center justify-center rounded-[12px]",
            color === "gray" && playerIconButtonInnerClassName
          )}
        >
          <PlayerIcon
            src={getVolumeIcon(color, volume, { active: isMuted })}
            size={24}
          />
        </span>
      </button>
      <span
        className={dividerVariants({
          color,
          orientation: "horizontal",
          size,
        })}
        aria-hidden
      />
    </div>
  )
}

function HorizontalVolumeSlider({
  color,
  volume,
  onVolumeChange,
}: {
  color: PlayerVolumeColor
  volume: number
  onVolumeChange: (volume: number) => void
}) {
  const volumePercent = Math.round(volume * 100)

  return (
    <div className={cn("flex items-center gap-[9px]", poppins.className)}>
      <PlayerVolumeTrack
        volume={volume}
        onVolumeValueChange={onVolumeChange}
        color={color}
        orientation="horizontal"
      />
      <span className={labelVariants({ color })}>{volumePercent}</span>
    </div>
  )
}

function ExpandedHorizontalVolumeControl({
  volume,
  onVolumeChange,
  color,
  toggleMute,
  isMuted,
  size,
}: {
  volume: number
  onVolumeChange: (volume: number) => void
  color: PlayerVolumeColor
  toggleMute: () => void
  isMuted: boolean
  size: ControlSize
}) {
  return (
    <div
      className={cn(
        containerVariants({ color, orientation: "horizontal", size }),
        "gap-5 pr-3"
      )}
    >
      <VolumeIconSection
        color={color}
        volume={volume}
        isMuted={isMuted}
        onToggleMute={toggleMute}
        size={size}
      />
      <HorizontalVolumeSlider
        color={color}
        volume={volume}
        onVolumeChange={onVolumeChange}
      />
    </div>
  )
}

function CollapsibleHorizontalVolumeControl({
  volume,
  onVolumeChange,
  color,
  toggleMute,
  isMuted,
  controlsVisible,
  controlsHideDelayMs,
  size,
}: {
  volume: number
  onVolumeChange: (volume: number) => void
  color: PlayerVolumeColor
  toggleMute: () => void
  isMuted: boolean
  controlsVisible?: boolean
  controlsHideDelayMs: number
  size: ControlSize
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const collapsibleId = useId()
  const [collapsedExpanded, setCollapsedExpanded] = useState(false)
  const collapsedWidth = size === "sm" ? 40 : 48

  const closePanel = useCallback(() => {
    setCollapsedExpanded(false)
  }, [])

  useCloseOnOutsidePointerDown(rootRef, collapsedExpanded, closePanel)

  useEffect(() => {
    if (controlsVisible !== false) return

    const timeoutId = window.setTimeout(closePanel, controlsHideDelayMs)
    return () => window.clearTimeout(timeoutId)
  }, [closePanel, controlsHideDelayMs, controlsVisible])

  return (
    <div ref={rootRef} className="relative shrink-0">
      <motion.div
        initial={false}
        className={cn(
          containerVariants({ color, orientation: "horizontal", size }),
          "overflow-hidden"
        )}
        animate={{ width: collapsedExpanded ? 224 : collapsedWidth }}
        transition={{ duration: VOLUME_COLLAPSE_DURATION, ease: "easeOut" }}
      >
        {collapsedExpanded ? (
          <div className="flex h-full items-center gap-5 pr-3">
            <VolumeIconSection
              color={color}
              volume={volume}
              isMuted={isMuted}
              onToggleMute={toggleMute}
              size={size}
            />
            <motion.div
              key="volume-slider"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{
                duration: VOLUME_COLLAPSE_DURATION,
                ease: "easeOut",
              }}
              className="min-w-0"
              id={collapsibleId}
            >
              <HorizontalVolumeSlider
                color={color}
                volume={volume}
                onVolumeChange={onVolumeChange}
              />
            </motion.div>
          </div>
        ) : (
          <button
            type="button"
            className={cn(
              // Border/shadow live on the parent container — avoid a double frame.
              "flex size-full items-center justify-center rounded-[12px] transition-colors",
              volumeIconButtonStateClassName(color, isMuted)
            )}
            aria-label="Volume"
            aria-pressed={isMuted}
            aria-expanded={false}
            aria-controls={collapsibleId}
            onClick={() => setCollapsedExpanded(true)}
          >
            <PlayerIcon
              src={getVolumeIcon(color, volume, { active: isMuted })}
              size={24}
            />
          </button>
        )}
      </motion.div>
    </div>
  )
}

function MuteOnlyVolumeButton({
  color,
  volume,
  isMuted,
  onToggleMute,
  size = "md",
}: {
  color: PlayerVolumeColor
  volume: number
  isMuted: boolean
  onToggleMute: () => void
  size?: ControlSize
}) {
  return (
    <button
      type="button"
      className={cn(
        blueVolumeIconButtonBaseClassName,
        blueVolumeIconButtonSizeClassName({ size }),
        volumeIconButtonStateClassName(color, isMuted)
      )}
      aria-label={isMuted ? "Unmute" : "Mute"}
      aria-pressed={isMuted}
      onClick={(event) => {
        event.stopPropagation()
        onToggleMute()
      }}
    >
      <PlayerIcon
        src={getVolumeIcon(color, volume, { active: isMuted })}
        size={24}
      />
    </button>
  )
}

export default function PlayerHorizontalVolumeControl({
  volume,
  onVolumeChange,
  color = "blue",
  defaultExpanded = true,
  controlsVisible,
  controlsHideDelayMs = 250,
  size = "md",
  muteOnly = false,
}: Props) {
  const { toggleMute, isMuted } = usePlayerVolumeMute(volume, onVolumeChange)

  if (muteOnly) {
    return (
      <MuteOnlyVolumeButton
        color={color}
        volume={volume}
        isMuted={isMuted}
        onToggleMute={toggleMute}
        size={size}
      />
    )
  }

  if (defaultExpanded) {
    return (
      <ExpandedHorizontalVolumeControl
        volume={volume}
        onVolumeChange={onVolumeChange}
        color={color}
        toggleMute={toggleMute}
        isMuted={isMuted}
        size={size}
      />
    )
  }

  return (
    <CollapsibleHorizontalVolumeControl
      volume={volume}
      onVolumeChange={onVolumeChange}
      color={color}
      toggleMute={toggleMute}
      isMuted={isMuted}
      controlsVisible={controlsVisible}
      controlsHideDelayMs={controlsHideDelayMs}
      size={size}
    />
  )
}
